import React, { useState, useEffect, useContext } from "react";
import {
  Save,
  Clock,
  Loader2,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import api from "../api/axiosInstance";
import { AuthContext } from "../App";

const BulkAttendanceManagement = () => {
  const { user } = useContext(AuthContext);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("get_employees.php")
      .then((res) => setEmployees(res.data.employees || []));
    api
      .get("get_projects.php")
      .then((res) => setProjects(res.data.projects || []));
  }, []);

  useEffect(() => {
    if (selectedEmp && selectedMonth) {
      generateCalendar();
      fetchExistingAttendance();
    }
  }, [selectedEmp, selectedMonth]);

  const generateCalendar = () => {
    const [year, month] = selectedMonth.split("-");
    const days = new Date(year, month, 0).getDate();
    const dateArray = Array.from(
      { length: days },
      (_, i) => `${selectedMonth}-${(i + 1).toString().padStart(2, "0")}`,
    );
    setDaysInMonth(dateArray);
  };

  const fetchExistingAttendance = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `get_employee_month_attendance.php?emp_id=${selectedEmp.id}&month=${selectedMonth}`,
      );
      const mapped = {};
      res.data.data.forEach((att) => {
        mapped[att.date] = { ...att };
      });
      setAttendanceData(mapped);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (date, field, value) => {
    setAttendanceData((prev) => ({
      ...prev,
      [date]: { ...prev[date], [field]: value },
    }));
  };

  const handleBulkSave = async () => {
    setSaving(true);
    try {
      await api.post("save_bulk_attendance.php", {
        employee_id: selectedEmp.id,
        month: selectedMonth,
        pay_type: selectedEmp.pay_type,
        attendance: attendanceData,
      });
      alert("Attendance Ledger Updated!");
    } catch (err) {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (date, dayData) => {
    // 1. DAILY PAY: Numeric Shift Input (0.5, 1, 1.5 etc)
    if (selectedEmp.pay_type === "Daily") {
      return (
        <input
          type="number"
          step="0.1"
          className="w-24 p-2 bg-slate-50 border border-slate-200 rounded-lg font-black text-center text-xs"
          placeholder="Shift (e.g. 1.2)"
          value={dayData.multiplier || ""}
          onChange={(e) => {
            const val = parseFloat(e.target.value) || 0;
            updateDay(date, "multiplier", val);
            updateDay(date, "status", val > 0 ? "Present" : "Absent");
          }}
        />
      );
    }

    // 2. MONTHLY PAY: Status Toggles (P, A, HD)
    if (selectedEmp.pay_type === "Monthly") {
      const options = [
        { label: "P", val: 1, status: "Present", color: "bg-emerald-500" },
        { label: "HD", val: 0.5, status: "Present", color: "bg-amber-500" },
        { label: "A", val: 0, status: "Absent", color: "bg-rose-500" },
      ];
      return (
        <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
          {options.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => {
                updateDay(date, "multiplier", opt.val);
                updateDay(date, "status", opt.status);
              }}
              className={`px-4 py-1.5 rounded-lg font-black text-[10px] transition-all ${dayData.multiplier == opt.val ? `${opt.color} text-white shadow-md` : "text-slate-400 hover:bg-white"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    // 3. HOURLY PAY: Time In/Out
    if (selectedEmp.pay_type === "Hourly") {
      return (
        <div className="flex items-center gap-2">
          <input
            type="time"
            className="p-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-[10px]"
            value={dayData.in_time || ""}
            onChange={(e) => updateDay(date, "in_time", e.target.value)}
          />
          <span className="font-black text-slate-300">-</span>
          <input
            type="time"
            className="p-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-[10px]"
            value={dayData.out_time || ""}
            onChange={(e) => updateDay(date, "out_time", e.target.value)}
          />
        </div>
      );
    }
  };

  return (
    <div className="space-y-8 p-4 animate-in fade-in duration-500">
      <div className="flex flex-col lg:row justify-between items-start lg:items-center gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
            Master Attendance
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase italic tracking-widest">
            {`Monthly Bulk Editor for ${user.company_name}`}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            className="p-4 bg-white border border-slate-200 rounded-2xl font-black text-[10px] uppercase shadow-sm outline-none focus:border-indigo-500"
            onChange={(e) =>
              setSelectedEmp(employees.find((emp) => emp.id == e.target.value))
            }
          >
            <option value="">Choose Employee...</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} — {e.pay_type}
              </option>
            ))}
          </select>
          <input
            type="month"
            className="p-4 bg-white border border-slate-200 rounded-2xl font-black text-[10px] uppercase outline-none shadow-sm"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          {selectedEmp && (
            <button
              disabled={saving}
              onClick={handleBulkSave}
              className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-indigo-600 transition-all"
            >
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Save size={16} />
              )}{" "}
              Save Full Month
            </button>
          )}
        </div>
      </div>

      {selectedEmp && !loading ? (
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b border-slate-100">
              <tr>
                <th className="p-6">Date</th>
                <th className="p-6">Project / Site</th>
                <th className="p-6 text-center">
                  {selectedEmp.pay_type} Input
                </th>
                <th className="p-6 text-right">Preview</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {daysInMonth.map((date) => {
                const dayData = attendanceData[date] || {
                  status: "Absent",
                  multiplier: 0,
                  in_time: "00:00:00",
                  out_time: "00:00:00",
                  project_id: "",
                };
                const isSunday = new Date(date).getDay() === 0;
                return (
                  <tr
                    key={date}
                    className={`group hover:bg-slate-50/50 ${isSunday ? "bg-rose-50/20" : ""}`}
                  >
                    <td className="p-6">
                      <p className="font-black text-slate-900 text-sm italic">
                        {date.split("-")[2]}
                      </p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase leading-none">
                        {new Date(date).toLocaleDateString("en-US", {
                          weekday: "short",
                        })}
                      </p>
                    </td>
                    <td className="p-6">
                      <select
                        className="w-full p-3 bg-slate-50 border border-transparent rounded-xl font-bold text-[10px] uppercase outline-none focus:bg-white focus:border-slate-200 transition-all"
                        value={dayData.project_id || ""}
                        onChange={(e) =>
                          updateDay(date, "project_id", e.target.value)
                        }
                      >
                        <option value="">Select Project...</option>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.project_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-6 text-center flex justify-center">
                      {renderInput(date, dayData)}
                    </td>
                    <td className="p-6 text-right font-black text-slate-900 text-xs italic">
                      {selectedEmp.pay_type === "Hourly"
                        ? "Time Log"
                        : `₹${(selectedEmp.rate * (dayData.multiplier || 0)).toLocaleString()}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="h-96 flex items-center justify-center font-black text-slate-300 uppercase italic animate-pulse">
          Syncing Calendar...
        </div>
      ) : null}
    </div>
  );
};

export default BulkAttendanceManagement;
